import { Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import {
  EsraPipingAPIClient,
  ServiceDetailViewModel,
} from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
    selector: 'app-service-details-add-edit',
    templateUrl: './service-details-add-edit.component.html',
    styleUrls: ['./service-details-add-edit.component.scss'],
    standalone: false
})
export class ServiceDetailsAddEditComponent implements OnInit {
  inputFormGroup: UntypedFormGroup;
  serviceDetail: ServiceDetailViewModel | null = null;

  title: string = '';
  genericMissingValueMessage: string = 'This is a required field';

  submitClicked: boolean = false;
  isCreateMode: boolean = false;
  isEditMode: boolean = false;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<ServiceDetailsAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) private readonly serviceDetailData: ServiceDetailViewModel | null
  ) {
    this.inputFormGroup = this.formBuilder.group({
      name: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.serviceDetail = this.serviceDetailData ? new ServiceDetailViewModel(this.serviceDetailData) : null;
    this.isEditMode = this.serviceDetail !== null;
    this.isCreateMode = !this.isEditMode;

    if (this.isCreateMode) {
      this.title = 'Create a new service detail';
    }

    if (this.serviceDetail) {
      this.title = 'Edit service detail';
      this.setFormValues();
    }
  }

  private setFormValues(): void {
    if (!this.serviceDetail) {
      return;
    }

    this.inputFormGroup.setValue({
      name: this.serviceDetail.name,
    });
  }

  public save(): void {
    this.submitClicked = true;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
      return;
    }

    if (this.isCreateMode) {
        const newServiceDetail: ServiceDetailViewModel = new ServiceDetailViewModel({
          name: this.inputFormGroup.controls['name'].value,
          isActive: true,
        });

        this.esraApiClient.createServiceDetail(newServiceDetail).pipe(
          indicate(this.saving$),
          first()
        ).subscribe({
            next: () => {
              this.snackBarService.showSnackBar(
                true,
                `Service detail ${newServiceDetail.name} was successfully created.`
              );
              this.dialogRef.close(true);
            },
            error: () => {
              this.snackBarService.showSnackBar(
                true,
                `There may already be a service with the name, ${newServiceDetail.name}`
              )
            }
          });
    }

    const serviceDetail: ServiceDetailViewModel | null = this.serviceDetail;
    if (this.isEditMode && serviceDetail) {
        serviceDetail.name = this.inputFormGroup.controls['name'].value;
        this.esraApiClient.updateServiceDetail(serviceDetail).pipe(
          indicate(this.saving$),
          first()
        ).subscribe({
            next: () => {
              this.snackBarService.showSnackBar(
                true,
                `Service detail, ${serviceDetail.name}, was successfully updated`
              );
              this.dialogRef.close(true);
            },
            error: (err) => {
              this.snackBarService.showSnackBar(
                true,
                err.response,
                'mat-snack-bar-error'
              );
            }
          });
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
